import { Avatar, Box, Button, Typography, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import LeaderboardModal from './LeaderboardModal';
import FirstTrophy from './assets/trophy_1.png';
import SecondTrophy from './assets/trophy_2.png';
import ThirdTrophy from './assets/trophy_3.png';
import ChatIcon3 from '../../svg/ChatIcon3';
import { winners } from './winners';

const WinnerSection = () => {
  const theme = useTheme();

  const [openLeaderboardModal, setOpenLeaderboardModal] =
    useState<boolean>(false);

  const winnerData = useMemo<WinnerCardProps[]>(() => {
    return winners.map((user, idx) => ({
      prizeImage:
        idx === 0 ? FirstTrophy : idx === 1 ? SecondTrophy : ThirdTrophy,
      position: `${idx + 1}${['st', 'nd', 'rd'][idx] || 'th'} Place`,
      prize: idx === 0 ? '$1000' : idx === 1 ? `$600` : `$400`,
      profile: {
        fullName: user.full_name,
        image: user.profile_pic_url?.replace(/\"/g, ''),
        userName: user.user_name,
        score: user.score,
        quote: user.quote,
      },
    }));
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        padding: 4,
        width: { xs: '100%', md: 'auto' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <Typography variant='h3' color='text.primary'>
          Our Champions
        </Typography>
        <Typography variant='body1' color='text.secondary'>
          Everyone is a winner in our book. But some people won more than
          others.
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,

          width: { xs: '100%', md: 'auto' },
        }}
      >
        {winnerData.map((winner, idx) => (
          <WinnerCard key={idx} {...winner} />
        ))}
      </Box>
      <Button
        variant='outlined'
        onClick={() => setOpenLeaderboardModal(true)}
        sx={{
          border: `1px solid ${theme.palette.secondary._states.outlinedBorder} !important`,
          color: `${theme.palette.secondary.contrastText} !important`,
        }}
      >
        See Full Leaderboard
      </Button>
      <LeaderboardModal
        open={openLeaderboardModal}
        onClose={() => setOpenLeaderboardModal(false)}
      />
    </Box>
  );
};

export default WinnerSection;

interface WinnerCardProps {
  prizeImage: string;
  position: string;
  prize: string;
  profile: {
    fullName: string;
    image: string;
    userName: string;
    score: number;
    quote?: string;
  };
}

const WinnerCard: React.FC<WinnerCardProps> = (props) => {
  const { prizeImage, position, prize, profile } = props;
  const navigate = useNavigate();

  const handleChatWithPersonaClick = () => {
    navigate(`/${profile.userName}/chat`);
  };

  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'flex-start',
        justifyContent: { xs: 'center', md: 'space-between' },
        gap: 5,
        bgcolor: theme.palette.primary._states.hover,
        padding: 3,
        borderRadius: 5,
        width: '100%',
        maxWidth: { xs: 600, md: 1100 },
        mx: 'auto',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'row', md: 'column' },
          alignItems: 'center',
          justifyContent: { xs: 'space-between', md: 'center' },
          gap: 1,
          bgcolor: theme.palette.primary._states.hover,
          aspectRatio: { xs: 'auto', md: 1 },
          height: { xs: 'auto', md: '40dvh' },
          borderRadius: 3,
          width: { xs: '100%', md: '40dvh' },
          padding: 2,
        }}
      >
        <Box
          sx={{
            width: 'auto',
            height: { xs: '100px', md: '150px' },
            // bgcolor: theme.palette.background["container-brand-3"],
            borderRadius: 2,
          }}
        >
          {prizeImage && (
            <img src={prizeImage} width={'auto'} height={'100%'} />
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography variant='h6' color='text.primary'>
            {position}
          </Typography>
          <Typography variant='h5' color={theme.palette.primary.main}>
            {prize}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          width: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: { xs: 'center', md: 'flex-start' },
            gap: 2,
          }}
        >
          <Box>
            <Avatar
              src={profile.image}
              alt={profile.fullName}
              sx={{
                width: '204px',
                height: '193.21px',
                borderRadius: '86px',
                objectFit: 'cover',
              }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: { xs: 'center', md: 'flex-start' },
              height: 'stretch',
              py: 3,
            }}
          >
            <Box>
              <Typography variant='h5' color='text.primary'>
                {profile.fullName}
              </Typography>
              <Typography variant='h7' color='text.secondary'>
                @{profile.userName}
              </Typography>
              <Typography variant='h6' color='text.secondary'>
                Score: {profile.score}
              </Typography>
            </Box>
            <Button
              variant='contained'
              onClick={handleChatWithPersonaClick}
              startIcon={
                <ChatIcon3 fill={theme.palette.primary.contrastText} />
              }
              sx={{
                mt: 1,
                textTransform: 'none', // pill shape
                px: 2,
                py: 1,
              }}
            >
              Chat with @{profile.userName}
            </Button>
          </Box>
        </Box>
        {profile.quote && (
          <Box>
            <Typography variant='h6' color='text.primary'>
              {profile.quote}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
